<script setup lang="ts">
/**
 * A short list to pick one entry from and insert it — the panel of an editor
 * extension such as the signatures (I-2799). Arrows and Enter, a click or a
 * double click; a preview of the entry under the list. The extension brings
 * the entries and says whether they are still loading; the panel inserts the
 * chosen entry's HTML through the context and closes.
 */
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue';
import { NxButton } from '../Button';
import { i18nInjectionKey } from '../internal';
import type { NxRichTextExtensionContext } from './extensions';
import { richTextPreviewHtml } from './preview';

export interface NxRichTextChoice {
  id: string;
  /** The entry's name in the list. */
  label: string;
  /** What goes in at the caret. */
  html: string;
}

const props = withDefaults(
  defineProps<{
    context: NxRichTextExtensionContext;
    choices: readonly NxRichTextChoice[];
    /** Names the list for assistive technology („Signaturen“). */
    label: string;
    loading?: boolean;
    /** Why there is nothing to choose from — the source did not answer. */
    error?: string;
    /** Shown when the source answered with nothing. */
    emptyText?: string;
  }>(),
  { loading: false, error: '', emptyText: '' }
);

const i18n = inject(i18nInjectionKey, null);
function tr(key: string, fallback: string): string {
  const full = `common.rich_text.${key}`;
  const text = i18n?.t(full);
  return text && text !== full ? text : fallback;
}

const active = ref(0);
const list = ref<HTMLElement | null>(null);
const current = computed<NxRichTextChoice | undefined>(() => props.choices[active.value]);
const preview = computed(() => (current.value ? richTextPreviewHtml(current.value.html) : ''));

watch(
  () => props.choices,
  () => {
    if (active.value >= props.choices.length) active.value = 0;
  }
);

onMounted(() => void nextTick(() => list.value?.focus()));

async function insert(): Promise<void> {
  if (!current.value) return;
  await props.context.insert(current.value.html);
  props.context.close();
}

function move(step: number): void {
  if (!props.choices.length) return;
  active.value = Math.min(props.choices.length - 1, Math.max(0, active.value + step));
  void nextTick(() => {
    const row = list.value?.querySelector<HTMLElement>(`[data-index="${active.value}"]`);
    row?.scrollIntoView?.({ block: 'nearest' });
    // A clicked row holds the focus: it moves along, so Enter takes what is shown.
    if (row && document.activeElement?.closest('[role="option"]')) row.focus();
  });
}

function onKeydown(event: KeyboardEvent): void {
  if (event.isComposing) return;
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault();
    move(event.key === 'ArrowDown' ? 1 : -1);
  } else if (event.key === 'Enter') {
    const target = event.target instanceof HTMLElement ? event.target : null;
    const option = target?.closest<HTMLElement>('[role="option"]');
    // Enter on „Abbrechen“ or „Einfügen“ does what the button does.
    if (!option && target?.closest('button')) return;
    event.preventDefault();
    if (option) active.value = Number(option.dataset.index);
    void insert();
  } else if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    props.context.close();
  }
}
</script>

<template>
  <div class="nx-rich-text-choice" @keydown="onKeydown">
    <div
      ref="list"
      class="nx-rich-text-choice__list"
      role="listbox"
      tabindex="0"
      :aria-label="label"
      :aria-activedescendant="current ? `nx-rich-text-choice-${current.id}` : undefined"
    >
      <p v-if="error" class="nx-rich-text-choice__note" role="alert">{{ error }}</p>
      <p v-else-if="loading && !choices.length" class="nx-rich-text-choice__note" role="status">
        {{ tr('choice_loading', 'Loading …') }}
      </p>
      <p v-else-if="!choices.length" class="nx-rich-text-choice__note">
        {{ emptyText || tr('choice_empty', 'Nothing to choose from.') }}
      </p>
      <button
        v-for="(choice, index) in choices"
        :id="`nx-rich-text-choice-${choice.id}`"
        :key="choice.id"
        type="button"
        role="option"
        tabindex="-1"
        class="nx-rich-text-choice__row"
        :class="{ 'is-active': index === active }"
        :aria-selected="index === active"
        :data-index="index"
        @focus="active = index"
        @click="active = index"
        @dblclick="
          active = index;
          void insert();
        "
      >
        {{ choice.label }}
      </button>
    </div>
    <!-- eslint-disable-next-line vue/no-v-html -- sanitized, style sheets removed -->
    <div v-if="current" class="nx-rich-text-choice__preview" v-html="preview" />
    <div class="nx-rich-text-choice__actions">
      <NxButton height="s" variant="outline" @click="context.close()">
        {{ tr('cancel', 'Cancel') }}
      </NxButton>
      <NxButton height="s" :disabled="!current" @click="insert">
        {{ tr('choice_insert', 'Insert') }}
      </NxButton>
    </div>
  </div>
</template>

<style scoped>
.nx-rich-text-choice {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  width: calc(var(--nxt-panel-width-2xl) - var(--nxt-space-md) * 2);
  max-width: calc(90vw - var(--nxt-space-md) * 2);
}

.nx-rich-text-choice__list {
  display: flex;
  flex-direction: column;
  max-height: calc(var(--nxt-size-unit) * 10);
  overflow-y: auto;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  outline: none;
}

.nx-rich-text-choice__list:focus-visible {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring-soft);
}

.nx-rich-text-choice__row {
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border: 0;
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  background: transparent;
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  text-align: left;
  cursor: pointer;
}

.nx-rich-text-choice__row:last-child {
  border-bottom: 0;
}

.nx-rich-text-choice__row:hover,
.nx-rich-text-choice__row.is-active {
  background: var(--nxt-brand-subtle-bg);
}

.nx-rich-text-choice__note {
  margin: 0;
  padding: var(--nxt-space-sm);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}

.nx-rich-text-choice__preview {
  /* Inline styles of an entry stay inside the box, fixed positions included. */
  contain: layout paint;
  max-height: calc(var(--nxt-size-unit) * 10);
  overflow-y: auto;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-sunken-bg);
  font-size: var(--nxt-font-size-sm);
}

.nx-rich-text-choice__preview :deep(p) {
  margin: 0 0 var(--nxt-space-2xs);
}

.nx-rich-text-choice__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--nxt-space-xs);
}
</style>
